import {
  ScaleAnswerTypeDto,
  type ScaleQuestionAnswerDefinitionOptionDto,
  ScaleQuestionDefinitionDto,
  UserQuizQuestionAnswerDto,
  type UserScaleQuestionAnswerOptionDto,
} from '@/api/generated';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import { SliderUi } from '@/components/form/Slider.ui.tsx';
import { DomPurifyConfig } from '@/config/DomPurify.config.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import DOMPurify from 'dompurify';
import parseHtml from 'html-react-parser';
import React, { useState } from 'react';
import { Form } from 'react-bootstrap';

const CENTER_MID = 50;

interface ScaleQuestionProps {
  disabled: boolean;
  onAnswerSelected: (questionDefinition: UserQuizQuestionAnswerDto) => void;
  questionDefinition: {
    discriminatorType: 'SCALE';
  } & ScaleQuestionDefinitionDto;
}

const multiplyAnswerBy = (
  answer: UserScaleQuestionAnswerOptionDto,
  multiplier: number,
): UserScaleQuestionAnswerOptionDto => ({
  ...answer,
  value: answer.value * multiplier,
});

const getScaleDistances = (
  value: number,
  options: ScaleQuestionAnswerDefinitionOptionDto[],
): UserScaleQuestionAnswerOptionDto[] => {
  const hasCenter = options.some(option => option.type === ScaleAnswerTypeDto.Center);

  const leftAnswer = {
    type: ScaleAnswerTypeDto.Left,
    value,
  };
  const rightAnswer = {
    type: ScaleAnswerTypeDto.Right,
    value: 100 - value,
  };
  if (hasCenter) {
    const centerAnswer = {
      type: ScaleAnswerTypeDto.Center,
      value: Math.abs(CENTER_MID - value) * 2,
    };

    if (value <= CENTER_MID) {
      return [multiplyAnswerBy(leftAnswer, 2), centerAnswer];
    }

    return [centerAnswer, multiplyAnswerBy(rightAnswer, 2)];
  }

  return [leftAnswer, rightAnswer];
};

const sortOptions = (
  options: ScaleQuestionAnswerDefinitionOptionDto[],
): ScaleQuestionAnswerDefinitionOptionDto[] => {
  const order = {
    [ScaleAnswerTypeDto.Center]: 1,
    [ScaleAnswerTypeDto.Left]: 0,
    [ScaleAnswerTypeDto.Right]: 2,
  };

  return options.sort((a, b) => order[a.type] - order[b.type]);
};

const ScaleQuestionForm: React.FC<ScaleQuestionProps> = ({
  disabled,
  onAnswerSelected,
  questionDefinition,
}) => {
  const { t } = useTranslationWithPrefix('views.process.quizQuestions');
  const { answerDefinition, question } = questionDefinition;
  const [value, setValue] = useState<number>(CENTER_MID);

  const handleChange = (_event: Event, value: number[] | number) => {
    if (typeof value !== 'number') {
      return;
    }

    if (!Number.isNaN(value)) {
      setValue(value);
    }
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onAnswerSelected({
      answers: getScaleDistances(value, answerDefinition.options),
      discriminatorType: questionDefinition.discriminatorType,
    });
  };

  return (
    <Form className="flex flex-column gap-l" onSubmit={handleSubmit}>
      <p className="lead fw-bold">{parseHtml(DOMPurify.sanitize(question, DomPurifyConfig))}</p>
      <Form.Group className="flex flex-column gap-l">
        <div className="flex flex-column max-w-(--breakpoint-md) gap-l">
          {sortOptions(answerDefinition.options).map(option => (
            <output
              aria-hidden="true"
              className="lead"
              htmlFor={questionDefinition.discriminatorType}
              key={option.type}
            >
              {t(
                `scaleQuestions.${answerDefinition.options.length === 2 ? 'scale2' : 'scale3'}.${option.type}`,
              )}{' '}
              ) {option.answer}
            </output>
          ))}
        </div>
        <div className="flex flex-row w-100 justify-content-between">
          {sortOptions(answerDefinition.options).map(option => (
            <output
              aria-hidden="true"
              className="fw-bold lead"
              htmlFor={questionDefinition.discriminatorType}
              key={option.type}
            >
              {t(
                `scaleQuestions.${answerDefinition.options.length === 2 ? 'scale2' : 'scale3'}.${option.type}`,
              )}
            </output>
          ))}
        </div>
        <SliderUi
          disabled={disabled}
          max={100}
          min={1}
          name={questionDefinition.discriminatorType}
          onChange={handleChange}
          step={1}
          value={value}
        />
      </Form.Group>
      <ButtonWithLoading disabled={!value || disabled} type="submit">
        {t('buttons.next')}
      </ButtonWithLoading>
    </Form>
  );
};

export default ScaleQuestionForm;
